<script setup lang="ts">
import { default as PButton } from 'primevue/button'
import { default as PSelect } from 'primevue/select'
import { default as PInputText } from 'primevue/inputtext'
import { default as PMultiSelect } from 'primevue/multiselect'
import { default as PInputGroup } from 'primevue/inputgroup'
import { default as PInputGroupAddon } from 'primevue/inputgroupaddon'
import { UserGender } from '@/core/domain/resources/enums/UserGender.ts'
import { useGenderOptions, useRoleOptions } from '@/presentation/composables/dataOptions.ts'
import { computed } from 'vue'
import type { UserSearchRequestEntity } from '@/core/infrastructure/schemas/users/core/UserInputSchema.ts'
import { storeToRefs } from 'pinia'
import { useProfileStore } from '@/presentation/stores/profile/profile.ts'

// Modèles & évènements
const filters = defineModel<UserSearchRequestEntity>('filters', { required: true })
const props = defineProps<{ filtersLoading: boolean }>()
const emit = defineEmits<(e: 'submit') => void>()

// Types
const useYesNoOptions = computed(() => [
  { name: 'Oui', code: true },
  { name: 'Non', code: false }
])

// Store
const profileStore = useProfileStore()
const { getGender } = storeToRefs(profileStore)

/**
 * Déclenche l'évènement de soumission du formulaire
 * @param e
 */
function onSubmit() {
  emit('submit')
}
</script>

<template>
  <form class="col-span-12 grid grid-cols-12 gap-2" @submit.prevent="onSubmit" novalidate>
    <p-input-group class="col-span-12 h-fit md:col-span-2">
      <p-input-group-addon>
        <i class="pi pi-search" />
      </p-input-group-addon>
      <p-input-text
        v-model="filters.q"
        :disabled="props.filtersLoading"
        autocomplete="off"
        data-lpignore="true"
        data-form-type="other"
        placeholder="Filtrer par nom, prénom"
      />
    </p-input-group>
    <p-input-group class="col-span-12 h-fit md:col-span-3">
      <p-input-group-addon>
        <i class="pi pi-user" />
      </p-input-group-addon>
      <p-multi-select
        id="roles"
        v-model="filters.roles"
        :options="useRoleOptions"
        :showToggleAll="false"
        :showClear="true"
        :disabled="props.filtersLoading"
        :maxSelectedLabels="2"
        selectedItemsLabel="{0} éléments sélectionnés"
        display="chip"
        optionLabel="name"
        optionValue="code"
        aria-required="true"
        placeholder="Filtrer par rôles"
      />
    </p-input-group>
    <p-input-group class="col-span-12 h-fit md:col-span-2">
      <p-input-group-addon>
        <i :class="[getGender === UserGender.Values.FEMALE ? 'pi pi-venus' : 'pi pi-mars']" />
      </p-input-group-addon>
      <p-multi-select
        id="gender"
        v-model="filters.genders"
        :options="useGenderOptions"
        :showToggleAll="false"
        :showClear="true"
        :disabled="props.filtersLoading"
        :maxSelectedLabels="2"
        selectedItemsLabel="{0} éléments sélectionnés"
        display="chip"
        optionLabel="name"
        optionValue="code"
        aria-required="true"
        placeholder="Filtrer par genre"
      />
    </p-input-group>
    <p-input-group class="col-span-12 h-fit md:col-span-2">
      <p-input-group-addon>
        <i class="pi pi-lock" />
      </p-input-group-addon>
      <p-select
        id="blocked"
        v-model="filters.blocked"
        :options="useYesNoOptions"
        :showToggleAll="false"
        :showClear="true"
        :disabled="props.filtersLoading"
        display="chip"
        optionLabel="name"
        optionValue="code"
        aria-required="true"
        placeholder="Bloqué"
      />
    </p-input-group>
    <p-input-group class="col-span-12 h-fit md:col-span-2">
      <p-input-group-addon>
        <i class="pi pi-envelope" />
      </p-input-group-addon>
      <p-select
        id="emailVerified"
        v-model="filters.emailVerified"
        :options="useYesNoOptions"
        :showToggleAll="false"
        :showClear="true"
        :disabled="props.filtersLoading"
        display="chip"
        optionLabel="name"
        optionValue="code"
        aria-required="true"
        placeholder="Email vérifié"
      />
    </p-input-group>
    <p-button
      id="search"
      label="Filtrer"
      severity="primary"
      icon="pi pi-search"
      class="col-span-12 h-fit md:col-span-1"
      :loading="props.filtersLoading"
      type="submit"
    />
  </form>
</template>